import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { passwordResetRequestSchema } from '@perplexicon/validation';
import type { PasswordResetRequest } from '@perplexicon/validation';

import { Button, Card, ErrorState, Input } from '../../components/index.js';
import { useRequestPasswordReset } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';

export function ForgotPasswordPage() {
  const requestReset = useRequestPasswordReset();
  const {
    register: field,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetRequest>({ resolver: zodResolver(passwordResetRequestSchema) });

  const onSubmit = handleSubmit((values) => {
    requestReset.mutate(values);
  });

  if (requestReset.isSuccess) {
    return (
      <Card variant="parchment">
        <h1 className="font-display text-2xl font-semibold text-textPrimary">Check your email</h1>
        <p className="mt-4 text-sm text-textSecondary">{requestReset.data}</p>
        <Link
          to="/login"
          className="mt-4 inline-block text-sm font-semibold text-link hover:text-linkHover"
        >
          Back to log in
        </Link>
      </Card>
    );
  }

  return (
    <Card variant="parchment">
      <h1 className="font-display text-2xl font-semibold text-textPrimary">Reset your password</h1>
      <p className="mt-2 text-sm text-textSecondary">
        Enter the email address on your account and we&apos;ll send you a link to reset your
        password.
      </p>
      <form onSubmit={(e) => void onSubmit(e)} noValidate className="mt-4 flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...field('email')}
        />
        {requestReset.isError && <ErrorState message={getErrorMessage(requestReset.error)} />}
        <Button
          type="submit"
          emphasis="decorated"
          isLoading={requestReset.isPending}
          className="w-full"
        >
          Send reset link
        </Button>
      </form>
      <Link
        to="/login"
        className="mt-4 inline-block text-sm font-semibold text-link hover:text-linkHover"
      >
        Back to log in
      </Link>
    </Card>
  );
}
